import React, { useEffect, useState } from 'react';
import { Button, Dialog, Notice, TextInput } from '@origo/origo-ui';
import { ApiError } from '../../api/client';
import { useCreateBinding, useCreateFeatureService } from '../../api/hooks';
import type { DatasetSummary, SyncDirectionPolicy } from '../../api/types';
import { DirectionPolicyField } from '../bindings/DirectionPolicyField';

interface ProvisionDialogProps {
    open: boolean;
    onClose: () => void;
    system: string;
    dataset: DatasetSummary;
}

// Samme regel som BindingForm: 1–63 tegn, små bokstaver/tall/bindestrek,
// må starte med bokstav eller tall.
const BINDING_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,62}$/;

/** Lager en gyldig kobling-ID fra et fritekstnavn (slug). */
function toBindingId(name: string): string {
    return name
        .toLowerCase()
        .replace(/æ/g, 'ae')
        .replace(/ø/g, 'o')
        .replace(/å/g, 'a')
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 63)
        .replace(/-+$/, '');
}

/**
 * Dialog for å provisjonere et AGOL feature-service fra et datasett-skjema
 * (POST /agol/feature-services). Registrerer som standard også en kobling
 * (POST /admin/bindings) mot den nye tjenesten — kan slås av.
 */
export const ProvisionDialog: React.FC<ProvisionDialogProps> = ({ open, onClose, system, dataset }) => {
    const [serviceName, setServiceName] = useState(dataset.name);
    const [overwrite, setOverwrite] = useState(false);
    const [autoBind, setAutoBind] = useState(true);
    const [bindingId, setBindingId] = useState(() => toBindingId(dataset.name));
    // Ingen default: retning skal være et bevisst valg, også for en nyprovisjonert tjeneste.
    const [directionPolicy, setDirectionPolicy] = useState<SyncDirectionPolicy | null>(null);

    const createService = useCreateFeatureService();
    const createBinding = useCreateBinding();

    useEffect(() => {
        if (open) {
            setServiceName(dataset.name);
            setOverwrite(false);
            setAutoBind(true);
            setBindingId(toBindingId(dataset.name));
            setDirectionPolicy(null);
            createService.reset();
            createBinding.reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, dataset]);

    const trimmedBindingId = bindingId.trim();
    const bindingIdError =
        autoBind && trimmedBindingId && !BINDING_ID_PATTERN.test(trimmedBindingId)
            ? 'Kun små bokstaver, tall og bindestrek; må starte med bokstav/tall.'
            : undefined;

    const submit = async () => {
        try {
            const result = await createService.mutateAsync({
                forvaltningssystem: system,
                datasetId: dataset.datasetId,
                serviceName: serviceName.trim(),
                overwriteExisting: overwrite,
            });

            if (autoBind) {
                await createBinding.mutateAsync({
                    bindingId: trimmedBindingId,
                    displayName: serviceName.trim(),
                    forvaltningssystem: system,
                    centralDatasetId: dataset.datasetId,
                    agolBaseUrl: result.serviceUrl,
                    lokalIdField: 'lokalId',
                    enabled: true,
                    directionPolicy,
                });
            }
        } catch {
            /* feil vises via mutation.error under */
        }
    };

    const result = createService.data;

    const serviceError = createService.error instanceof ApiError
        ? createService.error.message
        : createService.error
            ? 'Provisjonering feilet.'
            : null;

    const bindingErrorMessage = createBinding.error instanceof ApiError
        ? createBinding.error.message
        : createBinding.error
            ? 'Koblingen kunne ikke registreres.'
            : null;

    const busy = createService.isPending || createBinding.isPending;
    const submitLabel = createBinding.isPending
        ? 'Registrerer kobling…'
        : createService.isPending
            ? 'Oppretter…'
            : 'Opprett feature-service';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title="Provisjoner AGOL feature-service"
            footer={
                result ? (
                    <Button variant="primary" onClick={onClose}>
                        Lukk
                    </Button>
                ) : (
                    <>
                        <Button variant="secondary" onClick={onClose}>
                            Avbryt
                        </Button>
                        <Button
                            variant="primary"
                            onClick={submit}
                            disabled={
                                busy ||
                                !serviceName.trim() ||
                                (autoBind && (!trimmedBindingId || !!bindingIdError || directionPolicy == null))
                            }
                        >
                            {submitLabel}
                        </Button>
                    </>
                )
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-04)' }}>
                {!result && (
                    <>
                        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-primary-70)' }}>
                            Oppretter et nytt feature-service i AGOL basert på skjemaet til datasettet
                            <strong> {dataset.name}</strong>. Sublag opprettes per objekttype/geometri.
                        </p>
                        <TextInput
                            label="Tjenestenavn"
                            value={serviceName}
                            onChange={(e) => setServiceName(e.target.value)}
                        />
                        <label style={{ display: 'flex', gap: 'var(--space-02)', alignItems: 'center', fontSize: 'var(--text-sm)' }}>
                            <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
                            Overskriv hvis tjenesten finnes
                        </label>

                        <label style={{ display: 'flex', gap: 'var(--space-02)', alignItems: 'center', fontSize: 'var(--text-sm)' }}>
                            <input type="checkbox" checked={autoBind} onChange={(e) => setAutoBind(e.target.checked)} />
                            Registrer kobling automatisk
                        </label>
                        {autoBind && (
                            <TextInput
                                label="Kobling-ID"
                                value={bindingId}
                                onChange={(e) => setBindingId(e.target.value)}
                                error={bindingIdError}
                                helperText="ID-en til den nye koblingen. 1–63 tegn: små bokstaver, tall og bindestrek. Kan ikke endres senere."
                            />
                        )}
                        {autoBind && (
                            <DirectionPolicyField
                                name="provision-direction-policy"
                                value={directionPolicy}
                                onChange={setDirectionPolicy}
                            />
                        )}
                    </>
                )}

                {serviceError && <Notice variant="danger">{serviceError}</Notice>}

                {result && (
                    <>
                        <Notice variant="success">
                            <div><strong>{result.serviceName}</strong> opprettet med {result.fieldCount} felt.</div>
                            <div style={{ marginTop: 'var(--space-01)', wordBreak: 'break-all' }}>{result.serviceUrl}</div>
                            {result.layerNames.length > 0 && (
                                <div style={{ marginTop: 'var(--space-01)' }}>Lag: {result.layerNames.join(', ')}</div>
                            )}
                        </Notice>

                        {autoBind && createBinding.isPending && (
                            <Notice variant="info">Registrerer kobling…</Notice>
                        )}
                        {autoBind && createBinding.data && (
                            <Notice variant="success">
                                Kobling <strong>{createBinding.data.bindingId}</strong> registrert.
                            </Notice>
                        )}
                        {autoBind && bindingErrorMessage && (
                            <Notice variant="danger">
                                Tjenesten ble opprettet, men koblingen kunne ikke registreres: {bindingErrorMessage} Du
                                kan opprette koblingen manuelt under Koblinger.
                            </Notice>
                        )}
                    </>
                )}
            </div>
        </Dialog>
    );
};
